import '@Pages/GeoMMM/ScenarioPlanning/ScenarioSparkCharts.scss';
import { ReactElement } from 'react';
import { formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  computeDisplayDeltaPercent,
  MIN_CHANGE_THRESHOLD,
  roundToCompactDollars,
  SCENARIO_NEGATIVE_PILL_COLOR
} from '@Components/ScenarioPlanningChart/scenario-planning-chart-utils';
import { PercentChange } from '@Components/PercentChange';
import { Size, StretchMode } from '@Components/designConstants';

export interface SparkChartCardProps {
  title: string;
  baselineValue: number;
  scenarioValue: number;
  prefix: string;
  decimals: number;
}

interface ScenarioSparkChartsProps {
  cards: SparkChartCardProps[];
  hasScenario: boolean;
  onEditScenario?: () => void;
}

const SparkChartCard = ({
  title,
  baselineValue,
  scenarioValue,
  prefix,
  decimals,
  hasScenario,
  onEditScenario
}: SparkChartCardProps & { hasScenario: boolean; onEditScenario?: () => void }): ReactElement => {
  // Round to the card's display precision so visually-identical values report 0%.
  const roundForDisplay = (value: number): number => {
    if (prefix === '$') {
      return roundToCompactDollars(value);
    }
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  };
  const percentChange = computeDisplayDeltaPercent(
    scenarioValue,
    baselineValue,
    roundForDisplay,
    prefix === '$' ? MIN_CHANGE_THRESHOLD : 0
  );
  const maxValue = Math.max(baselineValue, scenarioValue, 1);
  const baselineHeight = Math.max((baselineValue / maxValue) * 100, 5);
  const scenarioHeight = Math.max((scenarioValue / maxValue) * 100, 5);
  const formatValue = (value: number): string => {
    if (prefix !== '$') {
      return formatNumber(value, decimals);
    }
    const abs = Math.abs(value);
    if (abs >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(1)}M`;
    }
    if (abs >= 1_000) {
      return `$${(value / 1_000).toFixed(1)}K`;
    }
    return `$${Math.round(value)}`;
  };

  if (!hasScenario) {
    return (
      <div className='spark-chart-card'>
        <div className='spark-chart-header'>
          <div className='spark-chart-title'>{title}</div>
        </div>
        <div className='spark-chart-baseline-label'>Baseline: {formatValue(baselineValue)}</div>
        <button
          type='button'
          className='spark-chart-empty-prompt'
          onClick={onEditScenario}
          disabled={!onEditScenario}
        >
          Edit scenario to{'\n'}compare
        </button>
      </div>
    );
  }

  return (
    <div className='spark-chart-card'>
      <div className='spark-chart-header'>
        <div className='spark-chart-title'>{title}</div>
        <PercentChange
          stretchMode={StretchMode.FIT}
          negativeColor={SCENARIO_NEGATIVE_PILL_COLOR}
          percentChange={percentChange}
          size={Size.SM}
        />
      </div>
      <div className='spark-chart-bars'>
        <div className='spark-chart-column'>
          <div className='spark-chart-bar-container'>
            <div className='spark-chart-bar-value baseline'>{formatValue(baselineValue)}</div>
            <div className='spark-chart-bar-track'>
              <div className='spark-chart-bar-spacer' style={{ flexGrow: 100 - baselineHeight }} />
              <div className='spark-chart-bar baseline' style={{ flexGrow: baselineHeight }} />
            </div>
          </div>
          <div className='spark-chart-bar-label baseline'>Baseline</div>
        </div>
        <div className='spark-chart-column'>
          <div className='spark-chart-bar-container'>
            <div className='spark-chart-bar-value scenario'>{formatValue(scenarioValue)}</div>
            <div className='spark-chart-bar-track'>
              <div className='spark-chart-bar-spacer' style={{ flexGrow: 100 - scenarioHeight }} />
              <div className='spark-chart-bar scenario' style={{ flexGrow: scenarioHeight }} />
            </div>
          </div>
          <div className='spark-chart-bar-label scenario'>Scenario</div>
        </div>
      </div>
    </div>
  );
};

export const ScenarioSparkCharts = ({
  cards,
  hasScenario,
  onEditScenario
}: ScenarioSparkChartsProps): ReactElement => (
  <div className='scenario-spark-charts'>
    {cards.map(card => (
      <SparkChartCard
        key={card.title}
        {...card}
        hasScenario={hasScenario}
        onEditScenario={onEditScenario}
      />
    ))}
  </div>
);
